import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, WorkoutPlan, PlanItemRow } from '../../api';
import Icon, { IconName } from '../../design/Icon';
import { Ring, Sheet, fmtTime, fmtDateLong, fmtDateShort, todayStr } from '../../design/atoms';
import TargetSheet from '../../plan/TargetSheet';
import { WORKOUT_TYPES, fmtTarget, type TargetMap, type WorkoutType } from '../../plan/planShared';
import { FOCUS_GROUPS, EXERCISE_LIBRARY, picksForFocus, allExercises, type LibraryExercise } from '../../plan/exerciseLibrary';

/* SPEC 10/11 — one day of the plan: one-tap "as planned", custom actuals, and
   mid-session additions. Logging is allowed even on a trainer-owned plan.

   Serves both /app/plan (today, no date param) and /app/session/:date, which
   the calendar feed links every day to. Future days render read-only —
   logging opens on the day itself. */

const TYPE_ICONS: Record<string, IconName> = {
  strength: 'dumbbell', body: 'user', run: 'flame', erg: 'heart', circuit: 'spark', hold: 'clock', flow: 'leaf',
};

const sameMap = (a: TargetMap | null, b: TargetMap | null) => {
  if (!a || !b) return false;
  const ka = Object.keys(a), kb = Object.keys(b);
  return ka.length === kb.length && ka.every(k => (a as any)[k] === (b as any)[k]);
};

export default function MemberPlanTodayPage() {
  const { token } = useMemberAuth();
  const navigate = useNavigate();
  const { date: dateParam } = useParams<{ date?: string }>();
  const today = todayStr();
  /** The day being shown — today unless the calendar routed us to a date. */
  const date = dateParam ?? today;
  const isFuture = date > today;

  const [plan, setPlan] = useState<WorkoutPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logging, setLogging] = useState<string | null>(null);
  const [editing, setEditing] = useState<PlanItemRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('Suggested');

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    mfetch(token, '/member/plan/active')
      .then(r => setPlan((r.data ?? null) as WorkoutPlan | null))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load your plan'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  const day = plan?.days.find(d => d.date === date) ?? null;
  const locked = !!plan && plan.createdBy !== 'member' && !plan.allowMemberEdits;

  const progress = useMemo(() => {
    const items = day?.items ?? [];
    const done = items.filter(i => i.status === 'done').length;
    return { done, total: items.length, pct: items.length ? Math.round((done / items.length) * 100) : 0 };
  }, [day]);

  const replaceItem = (updated: PlanItemRow) =>
    setPlan(prev => prev && ({
      ...prev,
      days: prev.days.map(d => ({ ...d, items: d.items.map(i => (i.id === updated.id ? updated : i)) })),
    }));

  const logActual = async (item: PlanItemRow, actual: TargetMap) => {
    if (!token) return;
    setLogging(item.id);
    try {
      const r = await mfetch(token, `/member/plan/items/${item.id}/actual`, { method: 'POST', body: { actual } });
      replaceItem(r.data as PlanItemRow);
      setEditing(null);
    } catch (e) {
      toast.error(e instanceof MemberApiError ? e.message : 'Could not log that');
    } finally {
      setLogging(null);
    }
  };

  const addMidSession = async (e: LibraryExercise) => {
    if (!token || !day) return;
    try {
      const r = await mfetch(token, `/member/plan/days/${day.id}/items`, {
        method: 'POST',
        body: { exerciseName: e.name, workoutType: e.workoutType, target: e.target, actual: e.target },
      });
      const created = r.data as PlanItemRow;
      setPlan(prev => prev && ({
        ...prev,
        days: prev.days.map(d => (d.id === day.id ? { ...d, items: [...d.items, created] } : d)),
      }));
      setAdding(false);
      setSearch('');
    } catch (err) {
      toast.error(err instanceof MemberApiError ? err.message : 'Could not add that');
    }
  };

  /** 'Suggested' is the day's own focus pool; the rest are library groups. */
  const groups = useMemo(() => ['Suggested', ...FOCUS_GROUPS], []);

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) return allExercises().filter(e => e.name.toLowerCase().includes(q)).slice(0, 25);
    const pool =
      group === 'Suggested'
        ? (day && !day.isRest ? picksForFocus(day.focus, 9) : [])
        : EXERCISE_LIBRARY[group] ?? [];
    // Fall back to the whole library when a rest day has no focus pool.
    return (pool.length ? pool : allExercises()).slice(0, 25);
  }, [search, group, day]);

  const stateChip = (item: PlanItemRow) => {
    if (item.status !== 'done') return <span className="tag">Not logged</span>;
    if (item.source === 'added_mid_session') return <span className="tag sage">Added</span>;
    if (sameMap(item.actual, item.target)) return <span className="tag sage">As planned</span>;
    return <span className="tag sage">Logged</span>;
  };

  if (loading) {
    return (
      <div className="mpage">
        <div className="skel" style={{ height: 44, marginTop: 8 }} />
        <div className="skel" style={{ height: 110 }} />
        {[0, 1, 2, 3].map(i => <div key={i} className="skel" style={{ height: 62 }} />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Today's workout</div>
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Workout plan</div>
        <div className="card dashed" style={{ textAlign: 'center', padding: 26 }}>
          <div className="mid">No plan yet</div>
          <div className="sub wrap" style={{ marginTop: 4 }}>
            Pick a shape, arrange the days, and you're set. Takes about a minute.
          </div>
        </div>
        <button className="btn gold block" onClick={() => navigate('/app/plan/new')}>Plan my week</button>
      </div>
    );
  }

  return (
    <div className="mpage">
      <div className="between" style={{ paddingTop: 8 }}>
        <div className="row" style={{ gap: 10, minWidth: 0 }}>
          {dateParam && (
            <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
              <Icon n="left" s={18} c="var(--ink-2)" />
            </button>
          )}
          <div className="col" style={{ gap: 1, minWidth: 0 }}>
            <div className="dp dp-m trunc">
              {day ? `${fmtDateLong(date).split(',')[0]} · ${day.isRest ? 'Rest' : day.focus}` : fmtDateShort(date)}
            </div>
            <div className="row" style={{ gap: 6, minWidth: 0 }}>
              <span className="tiny trunc">
                {day?.startTime ? fmtTime(day.startTime) : 'Any time'}
                {locked ? " · trainer's plan" : ''}
              </span>
              {isFuture && <span className="tag">Upcoming</span>}
              {locked && <span className="tag gold">Trainer</span>}
            </div>
          </div>
        </div>
        <button className="icon-btn" aria-label="Calendar" onClick={() => navigate('/app/calendar')}>
          <Icon n="cal" s={18} c="var(--ink-2)" />
        </button>
      </div>

      {isFuture && day && (
        <div className="card dashed" style={{ padding: 12 }}>
          <div className="sub wrap">
            This session is planned for {fmtDateShort(date)} — logging opens on the day.
          </div>
        </div>
      )}

      {locked && (
        <div className="card tint" style={{ padding: 12 }}>
          <div className="row" style={{ gap: 9 }}>
            <div className="li gold"><Icon n="user" s={16} c="var(--gold-2)" /></div>
            <div className="sub wrap">Your trainer owns this plan — ask them to change it. You can still log what you do.</div>
          </div>
        </div>
      )}

      {!day || (day.isRest && day.items.length === 0) ? (
        <>
          <div className="card dashed" style={{ textAlign: 'center', padding: 26 }}>
            <div className="mid">
              {day?.isRest ? 'Rest day' : dateParam ? 'Nothing planned for this day' : 'Nothing planned today'}
            </div>
            <div className="sub wrap" style={{ marginTop: 4 }}>
              {day?.isRest
                ? 'Recovery counts — come back tomorrow.'
                : dateParam
                  ? 'Plan a week from the calendar to see it here.'
                  : 'Your plan has no entry for today.'}
            </div>
          </div>
          <button className="btn ghost block" onClick={() => navigate('/app/calendar')}>Open my calendar</button>
        </>
      ) : (
        <>
          <div className="card" style={{ padding: 16 }}>
            <div className="between">
              <div className="col" style={{ gap: 4 }}>
                <div className="lbl">Session</div>
                <div className="mid">{progress.done}/{progress.total} logged</div>
                <div className="tiny">
                  {isFuture
                    ? 'Logging opens on the day'
                    : progress.done === progress.total ? 'All done — nice work' : 'Tap a tick to log as planned'}
                </div>
              </div>
              <Ring v={progress.pct} size={62} w={6}>
                <div className="num" style={{ fontSize: 15 }}>{progress.pct}%</div>
              </Ring>
            </div>
          </div>

          <div className="card" style={{ padding: '5px 16px' }}>
            {day.items.map(item => {
              const done = item.status === 'done';
              return (
                <div key={item.id} className="lrow">
                  <button
                    aria-label={done ? `${item.exerciseName} logged` : `Log ${item.exerciseName} as planned`}
                    disabled={logging === item.id || isFuture}
                    onClick={() => !done && !isFuture && logActual(item, item.target)}
                    style={{
                      width: 26, height: 26, flex: '0 0 26px', borderRadius: 100, display: 'grid', placeItems: 'center',
                      border: `1.5px solid ${done ? 'var(--sage)' : 'var(--line-2)'}`,
                      background: done ? 'var(--sage)' : 'transparent',
                    }}
                  >
                    {done && <Icon n="check" s={15} c="var(--card)" />}
                  </button>
                  <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                    <div className="mid trunc">{item.exerciseName}</div>
                    <div className="row" style={{ gap: 6, minWidth: 0 }}>
                      <span className="tiny trunc">{fmtTarget(item.actual ?? item.target)}</span>
                      {stateChip(item)}
                    </div>
                  </div>
                  {!isFuture && (
                    <button
                      className="icon-btn"
                      aria-label={`Edit ${item.exerciseName}`}
                      onClick={() => setEditing(item)}
                    >
                      <Icon n="edit" s={16} c="var(--ink-2)" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {!isFuture && (
            <button className="card dashed" style={{ padding: 14, textAlign: 'center' }} onClick={() => setAdding(true)}>
              <div className="mid">＋ Add something you did</div>
            </button>
          )}

          <button className="btn gold block" onClick={() => navigate('/app/calendar')}>
            {isFuture ? 'Back to calendar' : 'Finish session'}
          </button>
        </>
      )}

      {editing && (
        <TargetSheet
          exerciseName={editing.exerciseName}
          workoutType={editing.workoutType as WorkoutType}
          initial={editing.actual ?? editing.target}
          mode="actual"
          saving={logging === editing.id}
          onClose={() => setEditing(null)}
          onSave={actual => logActual(editing, actual)}
        />
      )}

      {adding && (
        <Sheet onClose={() => { setAdding(false); setSearch(''); }}>
          <div className="col" style={{ gap: 2 }}>
            <div className="lbl">Mid-session</div>
            <div className="dp dp-m">Add what you did</div>
          </div>
          <input
            className="minput"
            placeholder="Search exercises"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="hscroll">
            {groups.map(g => (
              <button key={g} className={`chip${group === g ? ' on' : ''}`} onClick={() => setGroup(g)}>{g}</button>
            ))}
          </div>
          <div className="col" style={{ maxHeight: '42dvh', overflowY: 'auto' }}>
            {suggestions.map(e => (
              <button key={e.name} className="lrow" onClick={() => addMidSession(e)}>
                <div className="li gold"><Icon n={TYPE_ICONS[e.workoutType] ?? 'dumbbell'} s={16} c="var(--gold-2)" /></div>
                <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
                  <div className="mid trunc">{e.name}</div>
                  <div className="tiny trunc">{WORKOUT_TYPES[e.workoutType]?.label} · {fmtTarget(e.target)}</div>
                </div>
                <Icon n="plus" s={17} c="var(--gold-2)" />
              </button>
            ))}
          </div>
          <div className="tiny wrap">
            Added work is logged against today only — your plan for next week stays as it is.
          </div>
        </Sheet>
      )}
    </div>
  );
}
